/**
 * useActionStates — global hook that fetches `/api/credits/action-states`
 * once on mount and exposes helpers to gate UI buttons.
 *
 *   const { isHidden, isFree, isPaid, state } = useActionStates();
 *   if (isHidden('connect_to_lead')) return null;
 *
 * Action states are admin-controlled via the Credit Engine → Action Costs
 * tab (state: 'paid' | 'free' | 'hidden'). When an action is `hidden`:
 *   • UI removes the button entirely
 *   • Backend rejects with 403 (defence-in-depth — see check_action_state)
 * When `free`:
 *   • UI shows the button without price labels
 *   • Backend skips the credit deduction
 *
 * The result is cached for the React tree's lifetime — single network call.
 * Falls back to 'paid' (existing behaviour) on any error so toggles never
 * accidentally hide an action that's supposed to be live.
 */

import { useState, useEffect, useCallback, createContext, useContext, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';

const ActionStatesContext = createContext({ states: {}, loaded: false });

export function ActionStatesProvider({ children }) {
  const [states, setStates] = useState({});
  const [loaded, setLoaded] = useState(false);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    let alive = true;
    axios.get(`${API}/credits/action-states`).then(res => {
      if (alive && res.data?.states) {
        setStates(res.data.states);
      }
    }).catch(() => {
      // Silent failure — assume default 'paid' state for every action.
    }).finally(() => {
      if (alive) setLoaded(true);
    });
    return () => { alive = false; };
  }, []);

  // Allow imperative refresh after admin edits (rare).
  const refresh = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/credits/action-states`);
      if (res.data?.states) setStates(res.data.states);
    } catch { /* swallow */ }
  }, []);

  return (
    <ActionStatesContext.Provider value={{ states, loaded, refresh }}>
      {children}
    </ActionStatesContext.Provider>
  );
}

export function useActionStates() {
  const ctx = useContext(ActionStatesContext);
  const getState = (key) => ctx.states[key] || 'paid';
  return {
    states: ctx.states,
    loaded: ctx.loaded,
    refresh: ctx.refresh,
    state: getState,
    isHidden: (key) => getState(key) === 'hidden',
    isFree: (key) => getState(key) === 'free',
    isPaid: (key) => getState(key) === 'paid',
  };
}
